import { api } from '../api';

// Types
export interface RealTimeScores {
  clarity: number;
  pace: number;
  filler_words: number;
  confidence: number;
  energy: number;
  overall: number;
}

export interface PracticeSession {
  id: string;
  userId: string;
  scenarioId?: string;
  /** 'daily_challenge' is RETIRED: it may appear on historical rows only; nothing creates it. */
  type: 'free_practice' | 'scenario' | 'daily_challenge' | 'assessment' | 'npc_practice' | 'training';
  status: 'active' | 'paused' | 'completed' | 'cancelled';
  startedAt: string;
  endedAt?: string;
  /** null = not measured (never a fabricated 0). */
  durationSeconds: number | null;
  audioUrl?: string;
  transcript?: string;
  scores: RealTimeScores;
  /**
   * The session's ONE score (a canonical result row, else the canonical
   * evidence recorded at completion, else — pre-release rows only — the legacy
   * stored score; measured only). Present on list responses; prefer it over `scores`.
   */
  resolvedScore?: {
    overall: number | null;
    source: 'canonical' | 'canonical_evidence' | 'legacy';
    dimensions: Partial<Record<'clarity' | 'pace' | 'confidence' | 'energy' | 'filler_words', number>>;
  } | null;
  feedback?: Record<string, unknown>;
  wordCount: number;
  fillerWordCount: number;
  createdAt: string;
  updatedAt: string;
}

/** POST /practice/sessions/:id/discard → the discarded session (no scores). */
export interface DiscardedPracticeSession {
  id: string;
  type: string;
  status: 'cancelled';
  endedAt: string | null;
}

/**
 * The ONLY `type` values the web may send to POST /practice/sessions — exactly
 * the server's accepted set (practiceRoutes CLIENT_SESSION_TYPES; the backend
 * suite posts each one through the real router). The retired 'daily_challenge'
 * is a 422; a training session sends no type (trainingSessionRequest); NPC
 * sessions go through POST /practice/npc-session.
 */
export const CREATABLE_PRACTICE_SESSION_TYPES = ['free_practice', 'scenario', 'assessment', 'npc_practice'] as const;
export type CreatablePracticeSessionType = (typeof CREATABLE_PRACTICE_SESSION_TYPES)[number];

/**
 * AI-coach (classic) practice runs on the PRACTICE runtime: a personal
 * practice session, turns via POST /voice/practice/sessions/:id/turn, then
 * POST /practice/sessions/:id/finalize (AI feedback for every type), counted
 * once as a completed practice session. It is personal free practice; there
 * is no separate stored type (the practice_sessions.type enum never had
 * 'ai_coached', so the old request was a 422 and the session never existed).
 */
export const AI_COACH_SESSION_TYPE: CreatablePracticeSessionType = 'free_practice';

export interface CreateSessionRequest {
  type?: CreatablePracticeSessionType;
  scenarioId?: string;
  /**
   * Practise one of the caller's OWN open training assignments. The server
   * derives org/module from the assignment row and refuses anyone else's.
   */
  trainingAssignmentId?: string;
}

/**
 * The body that starts a session for a training assignment (My Training →
 * Start / Continue / Retry Training). Only the assignment id: the server
 * derives the type ('training'), org and module from the caller's OWN open
 * assignment and its bound module version — the single source of truth — and
 * answers 404 for anyone else's. (POST /practice/sessions accepts `type` as
 * optional exactly when trainingAssignmentId is present.)
 */
export function trainingSessionRequest(trainingAssignmentId: string): CreateSessionRequest {
  return { trainingAssignmentId };
}

/**
 * What a finalized session did to its training assignment:
 * completed / failed — this session was judged and set that status;
 * unmeasured — a required score was not measured, so it was not judged and the
 * assignment stays where it was; already_completed — it was done before.
 */
export interface TrainingOutcome {
  assignmentId: string;
  assignmentStatus: string;
  outcome: 'completed' | 'failed' | 'unmeasured' | 'already_completed';
}

export interface FinalizeSessionRequest {
  transcript?: string;
  audioBase64?: string;
  durationSeconds: number;
  scenarioContext?: string;
}

/**
 * The score detail a finalize / finalize-npc response carries: the stored
 * record's dimensions WITHOUT the legacy heuristic `overall`, which the server
 * no longer answers (docs/LEGACY-SCORE-FIELDS.md). The attempt's ONE overall
 * is the response's `attemptScore` (= `canonicalEvidence.overall`). Typed
 * without `overall` so no page can read a legacy overall from it.
 */
export type FinalizedScoreDetail = Omit<RealTimeScores, 'overall'> & {
  /** The canonical evidence recorded with the completion (the attempt's result). */
  canonicalEvidence?: { overall: number | null; modality?: 'audio' | 'text' };
};

export interface FinalizeSessionResponse {
  /** The completed row, without its score blob (carried once, as `scores`). */
  session: Omit<PracticeSession, 'scores'>;
  scores: FinalizedScoreDetail;
  feedback: {
    summary: string;
    strengths: string[];
    improvements: string[];
    tips: string[];
    /** 'model' = written by the feedback LLM; 'rule_based_fallback' = templated tips (RC10). */
    feedbackSource?: 'model' | 'rule_based_fallback';
  };
  analysis: {
    wordCount: number;
    fillerWordCount: number;
    wordsPerMinute: number;
    fillerWords: string[];
    sttUsed?: boolean;
  };
  /** Present only for a session linked to a training assignment. */
  training?: TrainingOutcome;
  /**
   * THIS attempt's result (RC19): the canonical evidence overall the server
   * recorded — what history, analytics and the training assignment read.
   * null = not measured. The response's ONE overall (the legacy heuristic
   * overall is not answered). Absent from an older server.
   */
  attemptScore?: number | null;
  /** How the attempt's evidence was recorded: spoken ('audio') or written ('text'). */
  evidenceModality?: 'audio' | 'text';
}

export interface AnalyticsResponse {
  totalSessions: number;
  totalPracticeTime: number;
  /** The canonical average (each attempt once, measured only); null = nothing measured. */
  averageScore: number | null;
  /** Delivery dimensions averaged over the attempts that measured them; null = never measured. */
  averageDimensions?: {
    clarity: number | null;
    pace: number | null;
    confidence: number | null;
    energy: number | null;
  };
  improvements: Array<{
    metric: string;
    change: number;
    trend: 'up' | 'down';
  }>;
}

/** GET /practice/progress — a view of the canonical gamification overview. */
export interface ProgressResponse {
  /** XP actually earned (NPC level rewards). Never estimated from session counts. */
  totalXp: number;
  /** Earned badge keys (unified vocabulary, persisted). */
  badges: string[];
  streakDays: number;
  currentStreak: number;
  longestStreak: number;
  sessionsCompleted: number;
  /** null = nothing measured yet. */
  averageScore: number | null;
}

export interface StreaksResponse {
  currentStreak: number;
  longestStreak: number;
  lastPracticeDate: string | null;
}

export interface PaginatedResponse<T> {
  data: T[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const practiceApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // List practice sessions
    listPracticeSessions: builder.query<
      PaginatedResponse<PracticeSession>,
      { page?: number; limit?: number; type?: string; status?: string }
    >({
      query: (params) => ({
        url: '/practice/sessions',
        params,
      }),
      transformResponse: (response: ApiResponse<PracticeSession[]> & { meta?: any }) => ({
        data: response.data,
        page: response.meta?.page || 1,
        limit: response.meta?.limit || 20,
        total: response.meta?.total || 0,
        totalPages: response.meta?.totalPages || 1,
      }),
      providesTags: ['Sessions'],
    }),

    // Create a new practice session
    createPracticeSession: builder.mutation<PracticeSession, CreateSessionRequest>({
      query: (body) => ({
        url: '/practice/sessions',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<PracticeSession>) => response.data,
      invalidatesTags: ['Sessions'],
    }),

    // End a practice session
    endPracticeSession: builder.mutation<PracticeSession, string>({
      query: (id) => ({
        url: `/practice/sessions/${id}/end`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<PracticeSession>) => response.data,
      invalidatesTags: (_result, _error, id) => ['Sessions', { type: 'Sessions', id }],
    }),

    // Discard the caller's OWN unfinished (active/paused) session: it becomes
    // cancelled, is never scored and never counts as an attempt. Someone
    // else's id answers 404; a completed session answers 409.
    discardPracticeSession: builder.mutation<DiscardedPracticeSession, string>({
      query: (id) => ({
        url: `/practice/sessions/${id}/discard`,
        method: 'POST',
      }),
      transformResponse: (response: ApiResponse<DiscardedPracticeSession>) => response.data,
      invalidatesTags: (_result, _error, id) => ['Sessions', { type: 'Sessions', id }],
    }),

    // Finalize a practice session with transcript and scoring
    finalizePracticeSession: builder.mutation<
      FinalizeSessionResponse,
      { id: string; data: FinalizeSessionRequest }
    >({
      query: ({ id, data }) => ({
        url: `/practice/sessions/${id}/finalize`,
        method: 'POST',
        body: data,
      }),
      transformResponse: (response: ApiResponse<FinalizeSessionResponse>) => response.data,
      invalidatesTags: (_result, _error, { id }) => [
        'Sessions',
        { type: 'Sessions', id },
        'Progress',
        'TrainingAssignments',
      ],
    }),

    // Get analytics
    getPracticeAnalytics: builder.query<AnalyticsResponse, void>({
      query: () => '/practice/analytics',
      transformResponse: (response: ApiResponse<AnalyticsResponse>) => response.data,
      providesTags: ['Progress'],
    }),

    // Get streaks
    getPracticeStreaks: builder.query<StreaksResponse, void>({
      query: () => '/practice/streaks',
      transformResponse: (response: ApiResponse<StreaksResponse>) => response.data,
      providesTags: ['Progress'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListPracticeSessionsQuery,
  useCreatePracticeSessionMutation,
  useEndPracticeSessionMutation,
  useDiscardPracticeSessionMutation,
  useFinalizePracticeSessionMutation,
  useGetPracticeAnalyticsQuery,
  useGetPracticeStreaksQuery,
} = practiceApi;
